import React, { useState } from 'react';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { compose } from 'recompose';

import { ApiResponse, SnmpData } from 'constants/api';
import { NetworkRequestStatus, PortProvisioningActions, SnmpStatus } from 'constants/literals';
import { useTranslation } from 'react-i18next';
import RequestStatusRenderer from '../utils/RequestStatusRenderer';
import { BaseModalTemplate, FormModalButtons } from 'components/modals/BaseModalTemplate';
import Alert from 'reactstrap/lib/Alert';
import { getPortSnmpStatus } from 'utils/snmpUtils';
import DropdownItem from 'reactstrap/lib/DropdownItem';

interface InProps {
    portId: number;
    exchange: string;
}

interface EnhanceProps {
    snmpDataBinding: ApiDataBinding<ApiResponse<{ snmp_data: SnmpData[] }>>;
    portProvisioningActionsRequest: ApiDataBinding<ApiResponse<{}>>;
}

type Props = InProps & EnhanceProps;

const PortInfoRequestBounceModal = ({ portId, exchange, snmpDataBinding, portProvisioningActionsRequest }: Props) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const toggle = () => {
        setIsOpen(!isOpen);
    };

    const handleSubmit = () => {
        portProvisioningActionsRequest
            .perform(
                {
                    exchange: exchange,
                    portId: portId,
                },
                {
                    provisioning_action: PortProvisioningActions.bounce,
                }
            )
            .then((response) => {
                if (response.request.networkStatus === NetworkRequestStatus.Success) {
                    toggle();
                    snmpDataBinding.invalidateCache();
                }
            });
    };

    return (
        <>
            <BaseModalTemplate
                isOpen={isOpen}
                toggle={toggle}
                header={t('portProvisioningActionsModal.headerTitle', {
                    provisioningAction: PortProvisioningActions.bounce,
                })}
                body={
                    <Alert color="warning" className="mb-0">
                        <strong>{t('common.warning')}</strong>
                        <p>
                            {t('portProvisioningActionsModal.warningMessagePortAction', {
                                provisioningAction: PortProvisioningActions.bounce,
                            })}
                        </p>
                    </Alert>
                }
                footer={
                    <FormModalButtons
                        loading={portProvisioningActionsRequest.request.networkStatus === NetworkRequestStatus.Loading}
                        toggle={toggle}
                        onSubmit={handleSubmit}
                    />
                }
            />
            <RequestStatusRenderer
                request={snmpDataBinding}
                success={() => (
                    <PortRequestBounceBtn snmpData={snmpDataBinding.data!.data!.snmp_data} onClick={toggle} />
                )}
            />
        </>
    );
};

interface PortRequestBounceBtnProps {
    snmpData: SnmpData[];
    onClick: () => void;
}

const PortRequestBounceBtn = ({ snmpData, onClick }: PortRequestBounceBtnProps) => {
    const { t } = useTranslation();

    const isSnmpStatusNotDisabled = getPortSnmpStatus(snmpData) !== SnmpStatus.Disabled;

    return (
        <>
            {isSnmpStatusNotDisabled && (
                <DropdownItem onClick={onClick}>{t('portProvisioningActionsModal.requestBounce')}</DropdownItem>
            )}
        </>
    );
};

const enhance = compose<Props, InProps>(
    withApiData(
        {
            snmpDataBinding: 'getPortSnmpData',
            portProvisioningActionsRequest: 'putPortProvisioningAction',
        },
        (ownProps: InProps) => ({
            snmpDataBinding: { portId: ownProps.portId },
            portProvisioningActionsRequest: {
                exchange: ownProps.exchange,
                portId: ownProps.portId,
            },
        })
    )
);

export default enhance(PortInfoRequestBounceModal);
